type Options = ScrollIntoViewOptions & { offset?: number };

const defaultOptions: Options = {
    behavior: 'smooth'
};

/**
 * Use this hook to scroll to a specific element on the page.
 *
 * By default it will scroll to the top of an element with smooth behavior.
 * You can pass in an offset to scroll to a specific position.
 * You can also pass in any other options that are available for the
 * [scrollTo](https://developer.mozilla.org/en-US/docs/Web/API/Window/scrollTo) function.
 *
 * @example
 * const scroll = useScrollIntoView();
 *
 * scroll('[data-validation-error-anchor="shortDescription"]', {
 *     offset: 45
 * });
 *
 * @returns scroll function
 */
export const useScrollIntoView = () => {
    const scroll = (selector: string, options?: Options) => {
        let element;

        if (!selector) return;

        try {
            element = document.querySelector(selector);
        } catch {
            console.warn(
                `Invalid selector passed to useScrollIntoView hook: "${selector}"`
            );
            return;
        }
        if (!element) return;

        // scrollIntoView if perhaps preferred over scrollTo, but it doesn't
        // support the offset option.
        if (options?.offset) {
            const y =
                element.getBoundingClientRect().top +
                window.pageYOffset -
                options.offset;
            window.scrollTo({ top: y, ...defaultOptions, ...options });
            return;
        }

        element.scrollIntoView({ ...defaultOptions, ...options });
    };
    return scroll;
};
